import { useMemo, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useGetAdminMeQuery, useLogoutAdminMutation, appApi } from "../../features/api/appApi";
import { AdminButton, adminCn } from "./AdminSectionCard";

const links = [
  { to: "/admin/overview", label: "Overview" },
  { to: "/admin/events", label: "Events" },
  { to: "/admin/iupc", label: "IUPC" },
  { to: "/admin/alumni", label: "Alumni" },
  { to: "/admin/committee", label: "Committee" },
  { to: "/admin/hall", label: "Hall Of Shame" },
  { to: "/admin/recruitment", label: "Recruitment" },
  { to: "/admin/email-sender", label: "Email Sender" },
  { to: "/admin/july-contest", label: "July Contest" },
  { to: "/admin/contests", label: "Contests" },
  { to: "/admin/security", label: "Security" },
];

const AdminLayout = () => {
  const dispatch = useDispatch();
  const { admin } = useSelector((state) => state.adminAuth);
  const { data: me } = useGetAdminMeQuery();
  const [logoutAdmin] = useLogoutAdminMutation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const accountLabel = useMemo(() => (me && me.email) || admin?.email || "admin", [me, admin]);

  const refreshAll = () => {
    dispatch(
      appApi.util.invalidateTags([
        "AdminAuth",
        "AdminEvents",
        "AdminIupc",
        "AdminAlumni",
        "AdminCommittee",
        "AdminHall",
        "AdminRecruitment",
      ])
    );
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 px-3 py-4 sm:px-5 sm:py-6 lg:px-8">
      <div className="mx-auto grid w-full max-w-[1600px] gap-5 xl:grid-cols-[280px_minmax(0,1fr)] xl:gap-7">
        <aside className="hidden xl:sticky xl:top-4 xl:self-start xl:max-h-[calc(100vh-2rem)] xl:block xl:overflow-y-auto xl:pr-1">
          <div className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-[0_16px_34px_-20px_rgba(15,23,42,0.4)] sm:p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Admin Panel</p>
            <h1 className="mt-2 text-xl font-black tracking-tight text-slate-900 sm:text-2xl">GUBCPC Control</h1>
            <p className="mt-1 break-words text-xs text-slate-500">{(me && me.email) || admin?.email || "admin"}</p>

            <nav className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-1">
              {links.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    adminCn(
                      "inline-flex min-w-0 items-center justify-center rounded-xl border px-3 py-2 text-center text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100",
                      isActive
                        ? "border-slate-900 bg-slate-900 !text-white"
                        : "border-slate-200 bg-white !text-slate-800 hover:border-slate-300 hover:bg-slate-100"
                    )
                  }
                >
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </nav>

            <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
              <AdminButton variant="secondary" className="w-full" onClick={refreshAll}>
                Refresh Data
              </AdminButton>
              <AdminButton variant="danger" className="w-full" onClick={() => logoutAdmin()}>
                Logout
              </AdminButton>
            </div>
          </div>
        </aside>

        <div className="min-w-0 space-y-5 sm:space-y-6">
          <div className="sticky top-3 z-20 xl:hidden">
            <div className="rounded-2xl border border-slate-200/90 bg-white/90 p-3 shadow-[0_16px_34px_-20px_rgba(15,23,42,0.4)] backdrop-blur">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(true)}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100"
                  aria-label="Open navigation menu"
                >
                  <span className="flex flex-col gap-1.5">
                    <span className="h-0.5 w-5 rounded-full bg-current" />
                    <span className="h-0.5 w-5 rounded-full bg-current" />
                    <span className="h-0.5 w-5 rounded-full bg-current" />
                  </span>
                </button>

                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Admin Panel
                  </p>
                  <h1 className="truncate text-sm font-bold text-slate-900">GUBCPC Control</h1>
                  <p className="truncate text-xs text-slate-500">{accountLabel}</p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <span className="inline-flex h-3 w-3 rounded-full bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.12)]" />
                  <span className="hidden sm:inline text-xs font-semibold text-slate-500">Live</span>
                </div>
              </div>
            </div>
          </div>

          {mobileNavOpen ? (
            <div className="fixed inset-0 z-50 xl:hidden">
              <button
                type="button"
                aria-label="Close navigation menu"
                className="absolute inset-0 bg-slate-950/45 backdrop-blur-[2px]"
                onClick={() => setMobileNavOpen(false)}
              />
              <div className="absolute left-0 top-0 h-full w-[88vw] max-w-sm overflow-y-auto bg-white p-4 shadow-2xl">
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Navigation
                    </p>
                    <h2 className="mt-1 text-xl font-black tracking-tight text-slate-900">
                      GUBCPC Control
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobileNavOpen(false)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50"
                    aria-label="Close navigation"
                  >
                    ×
                  </button>
                </div>

                <div className="mt-4 space-y-4">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Account</p>
                    <p className="mt-1 break-words text-sm font-semibold text-slate-900">{accountLabel}</p>
                  </div>

                  <nav className="grid gap-2">
                    {links.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileNavOpen(false)}
                        className={({ isActive }) =>
                          adminCn(
                            "inline-flex items-center justify-between rounded-xl border px-4 py-3 text-sm font-semibold transition",
                            isActive
                              ? "border-slate-900 bg-slate-900 !text-white"
                              : "border-slate-200 bg-white !text-slate-800 hover:border-slate-300 hover:bg-slate-50"
                          )
                        }
                      >
                        <span>{item.label}</span>
                        <span className="text-xs opacity-70">Open</span>
                      </NavLink>
                    ))}
                  </nav>

                  <div className="grid grid-cols-1 gap-2">
                    <AdminButton variant="secondary" className="w-full" onClick={refreshAll}>
                      Refresh Data
                    </AdminButton>
                    <AdminButton variant="danger" className="w-full" onClick={() => logoutAdmin()}>
                      Logout
                    </AdminButton>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <Outlet />
        </div>
      </div>
    </main>
  );
};

export default AdminLayout;
